// Saved views dropdown for the Leads screen. A "view" is a snapshot of the
// current filter+sort state (query, status, filters, sort, order). The parent
// owns the live state — we just apply a view via `onApply(view)` and read
// `current` to know what to save.
//
// Views are per-user and scoped (default scope: "leads"). The dropdown shows
// the user's views, lets them apply or delete one, and offers "Save current
// view" which prompts for a name.

import React, { useState } from "react";
import toast from "react-hot-toast";
import { Bookmark, ChevronDown, Trash2, Plus, Check } from "lucide-react";
import {
  useSavedViews, useCreateSavedView, useDeleteSavedView,
} from "../hooks/useSavedViews";
import ConfirmDeleteModal from "../overlays/ConfirmDeleteModal";

export default function SavedViewsBar({ scope = "leads", current, onApply, activeViewId, onActiveChange }) {
  const [open, setOpen] = useState(false);
  const [confirmViewId, setConfirmViewId] = useState(null);
  const { data: views = [], isLoading } = useSavedViews(scope);
  const create = useCreateSavedView(scope);
  const del = useDeleteSavedView(scope);

  const activeView = views.find((v) => v._id === activeViewId) || null;
  const confirmView = confirmViewId ? views.find((v) => v._id === confirmViewId) : null;

  const apply = (view) => {
    onApply(view.config || {});
    if (onActiveChange) onActiveChange(view._id);
    setOpen(false);
  };

  const save = () => {
    const name = window.prompt("Name this view");
    if (!name || !name.trim()) return;
    create.mutate(
      { name: name.trim(), config: current },
      {
        onSuccess: (res) => {
          toast.success(`Saved "${name.trim()}"`);
          if (onActiveChange && res?.view?._id) onActiveChange(res.view._id);
          setOpen(false);
        },
        onError: (err) => toast.error(err?.response?.data?.error?.message || err.message || "Save failed"),
      },
    );
  };

  const remove = (view) => setConfirmViewId(view._id);

  const performRemove = () => {
    if (!confirmView) return;
    del.mutate(confirmView._id, {
      onSuccess: () => {
        toast.success("View deleted");
        if (activeViewId === confirmView._id && onActiveChange) onActiveChange(null);
        setConfirmViewId(null);
      },
      onError: (err) => toast.error(err?.response?.data?.error?.message || err.message || "Delete failed"),
    });
  };

  return (
    <div style={{ position: "relative" }}>
      {/* <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 28, padding: "0 10px", borderRadius: 8,
          border: "1px solid var(--color-border-subtle)",
          background: activeView ? "var(--crm-primary-soft)" : "var(--color-bg-surface)",
          color: activeView ? "var(--crm-primary)" : "var(--color-text-primary)",
          fontSize: 13, fontWeight: 500, cursor: "pointer",
        }}
      >
        <Bookmark size={13} />
        {activeView ? activeView.name : "Views"}
        <ChevronDown size={12} style={{ opacity: 0.6 }} />
      </button> */}
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 800 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 801,
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
            minWidth: 240, padding: 4,
          }}>
            <button
              onClick={save}
              disabled={create.isPending}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                textAlign: "left", padding: "8px 10px", borderRadius: 6,
                border: "none", background: "transparent", cursor: "pointer",
                fontSize: 13, color: "var(--crm-primary)", fontWeight: 500,
              }}
            >
              <Plus size={13} /> Save current view
            </button>
            <div style={{ height: 1, background: "var(--color-border-subtle)", margin: "4px 0" }} />
            {isLoading && (
              <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--color-text-secondary)" }}>Loading…</div>
            )}
            {!isLoading && views.length === 0 && (
              <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--color-text-secondary)" }}>No saved views yet</div>
            )}
            {views.map((v) => {
              const active = v._id === activeViewId;
              return (
                <div key={v._id} style={{
                  display: "flex", alignItems: "center", gap: 4,
                  borderRadius: 6, padding: "2px 2px 2px 0",
                  background: active ? "var(--crm-primary-soft)" : "transparent",
                }}>
                  <button
                    onClick={() => apply(v)}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", gap: 8,
                      textAlign: "left", padding: "8px 10px", borderRadius: 6,
                      border: "none", background: "transparent", cursor: "pointer",
                      fontSize: 13, color: "var(--color-text-primary)",
                    }}
                  >
                    {active ? <Check size={12} style={{ color: "var(--crm-primary)" }} /> : <span style={{ width: 12 }} />}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name}</span>
                  </button>
                  <button
                    onClick={() => remove(v)}
                    aria-label={`Delete ${v.name}`}
                    style={{
                      background: "transparent", border: "none",
                      padding: "6px 8px", cursor: "pointer",
                      color: "var(--color-text-secondary)", display: "inline-flex",
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
      <ConfirmDeleteModal
        open={!!confirmViewId}
        onClose={() => setConfirmViewId(null)}
        onConfirm={performRemove}
        title="Delete saved view?"
        message={confirmView ? `"${confirmView.name}" will be removed from your saved views.` : ""}
        confirmLabel="Delete view"
        isPending={del.isPending}
      />
    </div>
  );
}
